// src/features/executive/performance/widgets/AppointmentsCard.tsx
// Appointment statistics — full per-status breakdown grouped by lifecycle stage.
// The API returns a count for every appointment status (statusAppointment enum);
// this card shows each status that has data, organised under its stage group.
import { Box, Typography } from '@mui/material'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { pushDrill } from 'app/slice/executive.slice'
import WidgetCard from 'features/executive/components/WidgetCard/WidgetCard'
import { useAppointments } from '../hooks/use-appointments'
import { formatVariance } from 'utils/executive.utils'

// statusAppointment enum grouped by lifecycle stage. `statuses` is the row
// order within a group; a group with no data in the period is hidden.
const STATUS_GROUPS: { key: string; statuses: string[] }[] = [
  { key: 'upcoming', statuses: ['NORMAL', 'CONFIRM', 'WAITING_APPROVE'] },
  { key: 'inService', statuses: ['ARRIVED', 'PENDING_SERVICE', 'SERVICE', 'PENDING_PAYMENT', 'PAYMENT'] },
  { key: 'completed', statuses: ['DONE'] },
  { key: 'closed', statuses: ['CANCEL', 'CANCEL_APP', 'PUT_OFF', 'CAN_NOT_CONTACT', 'NOT_YET'] },
]

export default function AppointmentsCard() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { data, loading, error, refetch } = useAppointments()

  const title = t('EXECUTIVE.PERFORMANCE.APPOINTMENTS.TITLE')
  const isEmpty = !data || data.total === 0
  const handleCardClick = () => dispatch(pushDrill({ id: 'appointments', label: title }))

  const total = data?.total ?? 0
  const byStatus = data?.byStatus ?? {}
  const previousPeriod = data?.previousPeriod
  const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0)
  const totalVariance =
    previousPeriod && previousPeriod.total > 0
      ? (total - previousPeriod.total) / previousPeriod.total
      : null

  return (
    <WidgetCard
      title={title}
      titleTooltip={t(
        'EXECUTIVE.PERFORMANCE.APPOINTMENTS.TOOLTIP',
        'จำนวนนัดหมายแยกตามสถานะจริงในระบบ ในช่วงวันที่เลือก (นับตามวันนัด)\nจัดกลุ่มตามขั้นตอน: นัดล่วงหน้า · กำลังให้บริการ · เสร็จสิ้น · ยกเลิก/เลื่อน/ไม่มา\n% = สัดส่วนต่อยอดรวม — แสดงเฉพาะสถานะที่มีข้อมูลในช่วงนี้'
      )}
      loading={loading}
      error={error}
      empty={isEmpty}
      onRetry={refetch}
      onClick={handleCardClick}
      skeletonHeight={280}
      footer={
        <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled' }}>
          {t(
            'EXECUTIVE.PERFORMANCE.APPOINTMENTS.EMPTY_HINT',
            'แสดงเฉพาะสถานะที่มีข้อมูลในช่วงนี้'
          )}
        </Typography>
      }
    >
      {data && (
        <>
          {/* Total */}
          <Box
            sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              py: 1, borderBottom: 1, borderColor: 'divider',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {t('EXECUTIVE.PERFORMANCE.APPOINTMENTS.TOTAL')}
            </Typography>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {total.toLocaleString('th-TH')}
              </Typography>
              {totalVariance != null && (
                <Typography
                  variant="caption"
                  component="div"
                  color={totalVariance >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatVariance(totalVariance)}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Per-status, grouped by lifecycle stage — only statuses with data */}
          {STATUS_GROUPS.map((group) => {
            const rows = group.statuses.filter((s) => (byStatus[s] ?? 0) > 0)
            if (rows.length === 0) return null
            return (
              <Fragment key={group.key}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mt: 1.25, mb: 0.25, color: 'text.secondary', fontWeight: 600 }}
                >
                  {t(`EXECUTIVE.PERFORMANCE.APPOINTMENTS.GROUP.${group.key}`)}
                </Typography>
                {rows.map((s) => {
                  const count = byStatus[s] ?? 0
                  return (
                    <Box
                      key={s}
                      sx={{
                        display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', py: 0.5, pl: 1.5,
                      }}
                    >
                      <Typography variant="body2">
                        {t(`EXECUTIVE.PERFORMANCE.APPOINTMENTS.STATUS.${s}`)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {count.toLocaleString('th-TH')} ({pct(count).toFixed(1)}%)
                      </Typography>
                    </Box>
                  )
                })}
              </Fragment>
            )
          })}
        </>
      )}
    </WidgetCard>
  )
}
